/**
 * CSV周りの処理が複雑なため、関数をこのファイルに描く
 */

import {
  PRE_ENTRY_TIME_min,
  WAIT_INTERVIEWER_TIME_min,
} from "constants/timeValues";
import {
  CheckMonitor,
  MonitorManagementTableValueType,
} from "pages/MonitorManagement/hook";
import {
  CreateMonitorAssignInput,
  CreateMonitorAttributeInput,
  CreateMonitorInput,
  Interview,
  InterviewType,
  Monitor,
  MonitorAssign,
  MonitorAttribute,
  UpdateMonitorInput,
} from "types/graphqlapi";
import {
  createEmailMutation,
  deleteEmailMutation,
  fetchEmailBySubjectIdQuery,
  updateEmailMutation,
} from "utils/apis/email";
import {
  getInterviewByIdQuery,
  listInterviewsQuery,
} from "utils/apis/interview";
import {
  createMonitorMutation,
  fetchMonitorBysubjectIdQuery,
  updateMonitorMutation,
} from "utils/apis/monitor";
import {
  createMonitorAssignMutation,
  deleteMonitorAssignMutation,
  fetchMonitorAssignByMonitorIdQuery,
  fetchMonitorAssignBysubjectIdQuery,
  fetchMonitorLatestAssignByMonitorIdQuery,
} from "utils/apis/monitorAssign";
import {
  createMonitorAttributeMutation,
  deleteMonitorAttributeMutation,
  fetchMonitorAttributeByMonitorIdQuery,
  fetchMonitorAttributeBySubjectIdQuery,
} from "utils/apis/monitorAttribute";
import { getSubjectQuery } from "utils/apis/subject";
import {
  getInterviewUrl,
  isInterviewStarted,
  isInvalidEmail,
  makePinCode,
} from "utils/common";

/**---------------------------------------------------------------------------
 * 
 * 共通
 * 
 --------------------------------------------------------------------------------*/

const MODAL_ANS_ID_COLUMN_NO = 1;
export const MONITOR_MODAL_INTERVIEW_ANS_AND_DATE_COLUMN_NO = -1;

/* インタビュー取得 */
const fetchInterviewList = async (subjectId: string) => {
  const chatRoomList = await listInterviewsQuery(subjectId);
  if (chatRoomList === undefined) return [];
  return chatRoomList;
};

/* モニターテーブルに入っているモニターIDリスト取得関数  */
const fetchMonitorIdLIstInTargetSubject = async (subjectId: string) => {
  const monitorList = await fetchMonitorBysubjectIdQuery(subjectId);

  return monitorList.map((monitor) => {
    return {
      ansId: monitor.ans_id,
      monitorId: monitor.id,
    };
  });
};

/* ヘッダー復元関数 */
const createCSVHeader = async (subjectId: string, isPDH: boolean) => {
  const header = [
    "管理ID",
    "グループ / 割付",
    "開始日",
    "開始時刻",
    "終了日",
    "終了時刻",
    "実査No.",
    "URL",
    "PIN",
    "実績",
    "モニターID",
    ...(isPDH ? ["メールアドレス"] : []),
    "なまえ",
    "性別",
    "年齢",
    "居住地",
    "既婚有無",
    "子有無",
    "職業",
  ];
  const exportMonitorList = await fetchMonitorBysubjectIdQuery(subjectId);
  if (exportMonitorList === undefined || exportMonitorList.length === 0)
    return header;

  const variable = await fetchMonitorAttributeByMonitorIdQuery(
    exportMonitorList[0].id
  );
  if (variable === undefined) return header;
  variable.map((item) => {
    if (item !== null) {
      header.push(item.name);
    } else {
      header.push("");
    }
  });
  return header;
};

/**---------------------------------------------------------------------------
 * 
 * インポート
 * 
 --------------------------------------------------------------------------------*/

type nameAndvalue = {
  name: string;
  value: string;
};

type ErrorCheckResultModal = {
  errorFlg: boolean;
  data: {
    no: number;
    ansId?: string;
  }[];
};

export type ErrorModalMessageObject = {
  no: number;
  columnNo?: number;
  message: string;
};

type AlertCheckResultModal = {
  warningFlg: boolean;
  data: {
    no: number;
    email?: string;
  }[];
};

export type WarningModalMessageObject = ErrorModalMessageObject;

export type ChangeModalObject = {
  no: number;
  columnNo: number;
  columnString: string;
};

export type NewlyModalObject = {
  no: number;
};

/* エラーメッセージ作成関数 */
const makeErrorMessage = (
  data: { row?: number; column?: number; no?: number },
  message: string
): string => {
  if (data.no) {
    return `No.${data.no}：${message}`;
  } else {
    return `${data.row}行-${data.column ? data.column : ""}列：${message}`;
  }
};

/* インタビュー実施日比較のためのフォーマット変形 */
const formatDateForCompare = (str: string) => {
  const datePattern = new RegExp(
    "^[0-9]{4}/(0[1-9]|1[0-2]|[1-9])/(0[1-9]|[1-9]|[12][0-9]|3[01])$"
  );
  const dateStr = str.match(datePattern);
  if (!dateStr) return str;

  const list = str.split("/");
  const formattedMonth = list[1].padStart(2, "0");
  const formatDay = list[2].padStart(2, "0");

  return `${list[0]}-${formattedMonth}-${formatDay}`;
};

/* インタビュー時刻比較のためのフォーマット変形(hh:mm) */
const formatTimeForCompare = (str: string) => {
  const timePattern = new RegExp("^([0-9]|0[0-9]|1[0-9]|2[0-4]):[0-5][0-9]$");
  const timeStr = str.match(timePattern);
  if (!timeStr) return str;

  const parts = str.split(":");
  const hours = parts[0].padStart(2, "0");
  const minutes = parts[1].padStart(2, "0");

  return `${hours}:${minutes}`;
};

const convertTSVStringToTwoDimensionalArray = (inputString: string) => {
  return (
    inputString
      .split(/\r\n|\n/)
      .map((array) => array.split("\t"))
      .filter((array) => array.join().length >= 1)
      //DBと比較できるように時刻と時間の0埋めを行う
      .map((array, index) => {
        if (index === 0) return array;
        else {
          return array.map((item, index) => {
            if (index === 2 || index === 4) {
              return formatDateForCompare(item);
            } else if (index === 3 || index === 5) {
              return formatTimeForCompare(item);
            } else {
              return item;
            }
          });
        }
      })
  );
};

/* ANS_ID重複判定関数 */
const checkDuplicationAnsId = (
  array: string[][],
  errorModalMessageObjectList: ErrorModalMessageObject[]
) => {
  let errorFlg = false;
  const dataList: { no: number; ansId: string }[] = [];

  const ansIdList = [];
  for (let i = 0; i < array.length; i++) {
    if (i === 0) continue; //ヘッダー行はチェックしない
    if (
      errorModalMessageObjectList.find(
        ({ no, columnNo }) => no === i && columnNo === MODAL_ANS_ID_COLUMN_NO
      )
    )
      continue;
    const ansId = array[i][0];
    ansIdList.push(ansId);
  }

  for (let i = 0; i < ansIdList.length; i++) {
    let count = 0;
    const key = ansIdList[i];
    if (key === "") continue;
    for (let j = 0; j < ansIdList.length; j++) {
      if (key === ansIdList[j]) {
        count += 1;
      }
    }
    if (count > 1) {
      errorFlg = true;
      dataList.push({ no: i + 1, ansId: ansIdList[i] });
    }
  }

  const errorCheckResult: ErrorCheckResultModal = {
    errorFlg,
    data: dataList,
  };

  return errorCheckResult;
};

/* ANS_ID抜けているチェック */
const checkRequiredAnsId = (array: string[][]) => {
  let errorFlg = false;
  const dataList: { no: number }[] = [];
  for (let i = 0; i < array.length; i++) {
    if (i === 0) continue; //ヘッダー行はチェックしない
    const ansId = array[i][0];
    if (ansId === null || ansId === undefined || ansId === "") {
      errorFlg = true;
      dataList.push({ no: i });
    }
  }
  const errorCheckResult: ErrorCheckResultModal = {
    errorFlg,
    data: dataList,
  };
  return errorCheckResult;
};

/* 明細の行数が全て一致しているかチェック */
const checkContentLength = (array: string[][]) => {
  let errorFlg = false;
  const dataList: { no: number }[] = [];
  const headerLength = array[0].length;

  for (let i = 0; i < array.length; i++) {
    if (array[i].length !== headerLength) {
      errorFlg = true;
      dataList.push({ no: i });
    }
  }

  const errorCheckResult: ErrorCheckResultModal = {
    errorFlg,
    data: dataList,
  };
  return errorCheckResult;
};

/* アサイン日時空白チェック */
const checkBlankAssign = (array: string[][], subjectType: InterviewType) => {
  let errorFlg = false;
  const dataList: { no: number }[] = [];

  for (let i = 0; i < array.length; i++) {
    if (i === 0) continue; //ヘッダー行はチェックしない
    const interviewDateColumns = [];

    if (subjectType === InterviewType.ONE_TIME) {
      interviewDateColumns.push(array[i][2], array[i][3], array[i][5]);
    } else if (subjectType === InterviewType.TERM) {
      interviewDateColumns.push(
        array[i][2],
        array[i][3],
        array[i][4],
        array[i][5]
      );
    }

    const notBlankLength = interviewDateColumns.filter(
      (item) => item !== null && item !== undefined && item !== ""
    ).length;
    if (
      notBlankLength !== 0 &&
      notBlankLength !== interviewDateColumns.length
    ) {
      errorFlg = true;
      dataList.push({ no: i });
    }
  }

  const errorCheckResult: ErrorCheckResultModal = {
    errorFlg,
    data: dataList,
  };
  return errorCheckResult;
};

/* アサイン日時フォーマットチェック */
const checkDateTimeFormat = (
  array: string[][],
  errorModalMessageObjectList: ErrorModalMessageObject[],
  interviewSeqColumnNo: number,
  interviewDateTimeColumnNo: number,
  subjectType: InterviewType
) => {
  let errorFlg = false;
  const dataList: { no: number }[] = [];

  const interviewStartDateList = array.map((item) => {
    return item[2];
  });
  const interviewEndDateList = array.map((item) => {
    return item[4];
  });

  const interviewStartTimeList = array.map((item) => {
    return item[3];
  });
  const interviewEndTimeList = array.map((item) => {
    return item[5];
  });

  for (let i = 0; i < interviewStartDateList.length; i++) {
    if (i === 0) continue; //ヘッダー行はチェックしない
    if (
      errorModalMessageObjectList.find(
        ({ no, columnNo }) =>
          no === i &&
          (columnNo === interviewSeqColumnNo ||
            columnNo === interviewDateTimeColumnNo)
      )
    )
      continue;

    const datePattern = new RegExp(
      "^[0-9]{4}-(0[1-9]|1[0-2]|[1-9])-(0[1-9]|[1-9]|[12][0-9]|3[01])$"
    );

    const startDateResult =
      interviewStartDateList[i] === null ||
      interviewStartDateList[i] === undefined ||
      interviewStartDateList[i] === "" ||
      interviewStartDateList[i].match(datePattern);

    const endDateResult =
      interviewEndDateList[i] === null ||
      interviewEndDateList[i] === undefined ||
      interviewEndDateList[i] === "" ||
      interviewEndDateList[i].match(datePattern);

    const timePattern = new RegExp("^([0-9]|0[0-9]|1[0-9]|2[0-4]):[0-5][0-9]$");

    const startTimeResult =
      interviewStartTimeList[i] === null ||
      interviewStartTimeList[i] === undefined ||
      interviewStartTimeList[i] === "" ||
      interviewStartTimeList[i].match(timePattern);

    const endTimeResult =
      interviewEndTimeList[i] === null ||
      interviewEndTimeList[i] === undefined ||
      interviewEndTimeList[i] === "" ||
      interviewEndTimeList[i].match(timePattern);

    if (
      !startDateResult ||
      (subjectType === InterviewType.TERM && !endDateResult) || //ワンタイムの終了日列は「-」
      !startTimeResult ||
      !endTimeResult
    ) {
      errorFlg = true;
      dataList.push({ no: i });
    }
  }

  const errorCheckResult: ErrorCheckResultModal = {
    errorFlg,
    data: dataList,
  };
  return errorCheckResult;
};

/* アサインしようとする実査がDBにあるかチェック */
const checkExistInterview = (
  array: string[][],
  chatRoomList: Interview[],
  errorModalMessageObjectList: ErrorModalMessageObject[],
  interviewSeqColumnNo: number,
  interviewDateTimeColumnNo: number,
  subjectType: InterviewType
) => {
  let errorFlg = false;
  const dataList: { no: number }[] = [];

  array
    .map((item, index) => {
      if (index === 0) return; //ヘッダー行はチェックしない
      if (
        errorModalMessageObjectList.find(
          ({ no, columnNo }) =>
            no === index &&
            (columnNo === interviewSeqColumnNo ||
              columnNo === interviewDateTimeColumnNo)
        )
      )
        return;

      const startDate = item[2];
      const startTime = item[3];
      const endDate = item[4];
      const endTime = item[5];
      const interviewSeq = item[6];

      //ここのチェックでは実査が複数見つかっても良しとする
      if (subjectType === InterviewType.ONE_TIME) {
        if (interviewSeq || (startDate && startTime && endTime)) {
          const createdRoom = chatRoomList.find(
            (room) =>
              room.interview_seq.toString() === interviewSeq ||
              (room.scheduled_start_date === startDate &&
                room.scheduled_start_time === startTime &&
                room.scheduled_end_time === endTime)
          );
          if (!createdRoom) {
            errorFlg = true;
            dataList.push({ no: index });
          }
        }
      } else if (subjectType === InterviewType.TERM) {
        if (interviewSeq || (startDate && startTime && endDate && endTime)) {
          const createdRoom = chatRoomList.find(
            (room) =>
              room.interview_seq.toString() === interviewSeq ||
              (room.scheduled_start_date === startDate &&
                room.scheduled_start_time === startTime &&
                room.scheduled_end_date === endDate &&
                room.scheduled_end_time === endTime)
          );
          if (!createdRoom) {
            errorFlg = true;
            dataList.push({ no: index });
          }
        }
      }
    })
    .filter((item) => item !== undefined);

  const errorCheckResult: ErrorCheckResultModal = {
    errorFlg,
    data: dataList,
  };
  return errorCheckResult;
};

/* 実施済の実査にアサインされている人を別の実査にアサインしていないかチェック */
const checkAlreadyFinishedInterviewChange = (
  array: string[][],
  fetchedMonitorList: CheckMonitor[],
  errorModalMessageObjectList: ErrorModalMessageObject[],
  interviewSeqColumnNo: number,
  interviewDateTimeColumnNo: number,
  subjectType: InterviewType
) => {
  let errorFlg = false;
  const dataList: { no: number }[] = [];

  for (let i = 0; i < array.length; i++) {
    if (i === 0) continue; //ヘッダー行はチェックしない
    if (
      errorModalMessageObjectList.find(
        ({ no, columnNo }) =>
          no === i &&
          (columnNo === interviewSeqColumnNo ||
            columnNo === interviewDateTimeColumnNo)
      )
    )
      continue;

    const ansId = array[i][0];
    const targetMonitorId = fetchedMonitorList.find(
      ({ ans_id }) => ans_id === ansId
    )?.id;

    if (!targetMonitorId) continue;

    const targetInterview = fetchedMonitorList.find(
      ({ id }) => id === targetMonitorId
    )?.assignedInterview;

    if (!targetInterview) continue;

    if (subjectType === InterviewType.ONE_TIME) {
      if (
        targetInterview.interview_seq.toString() === array[i][6] ||
        (targetInterview.scheduled_start_date === array[i][2] &&
          targetInterview.scheduled_start_time === array[i][3] &&
          targetInterview.scheduled_end_time === array[i][5])
      ) {
        continue;
      }
    } else if (subjectType === InterviewType.TERM) {
      if (
        targetInterview.interview_seq.toString() === array[i][6] ||
        (targetInterview.scheduled_start_date === array[i][2] &&
          targetInterview.scheduled_start_time === array[i][3] &&
          targetInterview.scheduled_end_date === array[i][4] &&
          targetInterview.scheduled_end_time === array[i][5])
      ) {
        continue;
      }
    }

    if (targetInterview.actual_end_date) {
      errorFlg = true;
      dataList.push({ no: i });
    }
  }

  const errorCheckResult: ErrorCheckResultModal = {
    errorFlg,
    data: dataList,
  };
  return errorCheckResult;
};

/* 同じ時刻の実査が複数見つかったときに実査No.が未入力でないかチェック*/
const checkRequiredInterviewSeqExists = (
  array: string[][],
  chatRoomList: Interview[],
  errorModalMessageObjectList: ErrorModalMessageObject[],
  interviewSeqColumnNo: number,
  interviewDateTimeColumnNo: number,
  subjectType: InterviewType
) => {
  let errorFlg = false;
  const dataList: { no: number }[] = [];

  array
    .map((item, index) => {
      if (index === 0) return; //ヘッダー行はチェックしない
      if (
        errorModalMessageObjectList.find(
          ({ no, columnNo }) =>
            no === index &&
            (columnNo === interviewSeqColumnNo ||
              columnNo === interviewDateTimeColumnNo)
        )
      )
        return;

      const startDate = item[2];
      const startTime = item[3];
      const endDate = item[4];
      const endTime = item[5];
      const interviewSeq = item[6];

      if (subjectType === InterviewType.ONE_TIME) {
        if (startDate && startTime && endTime) {
          const sameDateInterviewList = chatRoomList.filter(
            (room) =>
              room.scheduled_start_date === startDate &&
              room.scheduled_start_time === startTime &&
              room.scheduled_end_time === endTime
          );
          if (sameDateInterviewList.length >= 2 && !interviewSeq) {
            errorFlg = true;
            dataList.push({ no: index });
          }
        }
      } else if (subjectType === InterviewType.TERM) {
        if (startDate && startTime && endDate && endTime) {
          const sameDateInterviewList = chatRoomList.filter(
            (room) =>
              room.scheduled_start_date === startDate &&
              room.scheduled_start_time === startTime &&
              room.scheduled_end_date === endDate &&
              room.scheduled_end_time === endTime
          );
          if (sameDateInterviewList.length >= 2 && !interviewSeq) {
            errorFlg = true;
            dataList.push({ no: index });
          }
        }
      }
    })
    .filter((item) => item !== undefined);

  const errorCheckResult: ErrorCheckResultModal = {
    errorFlg,
    data: dataList,
  };
  return errorCheckResult;
};

/* 実査日時とNo.の組み合わせの整合性をチェック*/
const checkDateAndNumberConsistence = (
  array: string[][],
  chatRoomList: Interview[],
  errorModalMessageObjectList: ErrorModalMessageObject[],
  interviewSeqColumnNo: number,
  interviewDateTimeColumnNo: number,
  subjectType: InterviewType
) => {
  let errorFlg = false;
  const dataList: { no: number }[] = [];

  array
    .map((item, index) => {
      if (index === 0) return; //ヘッダー行はチェックしない
      if (
        errorModalMessageObjectList.find(
          ({ no, columnNo }) =>
            no === index &&
            (columnNo === interviewSeqColumnNo ||
              columnNo === interviewDateTimeColumnNo)
        )
      )
        return;

      const startDate = item[2];
      const startTime = item[3];
      const endDate = item[4];
      const endTime = item[5];
      const interviewSeq = item[6];

      if (subjectType === InterviewType.ONE_TIME) {
        if (interviewSeq && startDate && startTime && endTime) {
          const consistentInterviewExists = chatRoomList.some(
            (room) =>
              room.interview_seq.toString() === interviewSeq &&
              room.scheduled_start_date === startDate &&
              room.scheduled_start_time === startTime &&
              room.scheduled_end_time === endTime
          );
          if (!consistentInterviewExists) {
            errorFlg = true;
            dataList.push({ no: index });
          }
        }
      } else if (subjectType === InterviewType.TERM) {
        if (interviewSeq && startDate && startTime && endDate && endTime) {
          const consistentInterviewExists = chatRoomList.some(
            (room) =>
              room.interview_seq.toString() === interviewSeq &&
              room.scheduled_start_date === startDate &&
              room.scheduled_start_time === startTime &&
              room.scheduled_end_date === endDate &&
              room.scheduled_end_time === endTime
          );
          if (!consistentInterviewExists) {
            errorFlg = true;
            dataList.push({ no: index });
          }
        }
      }
    })
    .filter((item) => item !== undefined);

  const errorCheckResult: ErrorCheckResultModal = {
    errorFlg,
    data: dataList,
  };
  return errorCheckResult;
};

/* メールアドレスフォーマットチェック */
const checkEmailFormat = async (array: string[][]) => {
  let errorFlg = false;
  const dataList: { no: number }[] = [];
  const emailList = array.map((item) => {
    return item[11];
  });

  for (let i = 0; i < emailList.length; i++) {
    if (i === 0) continue; //ヘッダー行はチェックしない

    if (await isInvalidEmail(emailList[i])) {
      errorFlg = true;
      dataList.push({ no: i });
    }
  }

  const errorCheckResult: ErrorCheckResultModal = {
    errorFlg,
    data: dataList,
  };
  return errorCheckResult;
};

/* ヘッダー変更判定関数 */
const detectHeaderChange = async (
  array: string[][],
  subjectId: string,
  previousMonitorAttributeList: MonitorAttribute[],
  isPDH: boolean
) => {
  if (
    previousMonitorAttributeList &&
    previousMonitorAttributeList.length >= 1
  ) {
    const importHeader = array[0];
    const previousHeader = await createCSVHeader(subjectId, isPDH);

    if (importHeader.length !== previousHeader.length) return true;
    for (let i = 0; i < importHeader.length; i++) {
      if (importHeader[i] !== previousHeader[i]) return true;
    }
    return false;
  } else {
    return false;
  }
};

/* 二次元配列をANS_IDをキーとしたマップに変換する */
const createOrganizedMap = (array: string[][]): Map<string, nameAndvalue[]> => {
  // ans_idをkeyとした、Mapへ変換
  const nameAndValueMap = new Map();
  for (let i = 0; i < array.length; i++) {
    if (i === 0) continue;
    const ans_id = array[i][0];
    const key = ans_id;
    const valueList = [];
    for (let j = 0; j < array[0].length; j++) {
      const value = {
        name: array[0][j],
        value: array[i][j],
      };
      valueList.push(value);
    }

    nameAndValueMap.set(key, valueList);
  }
  return nameAndValueMap;
};

const checkMonitorArrayBeforeImport = async (
  array: string[][],
  chatRoomList: Interview[],
  fetchedMonitorList: CheckMonitor[],
  subjectType: InterviewType,
  isPDH: boolean
) => {
  const errorModalMessageObjectList: ErrorModalMessageObject[] = [];

  const potentialEmailColumnNo = 3;

  // 明細の列数判定
  const errorContentResult = checkContentLength(array);
  if (errorContentResult.errorFlg) {
    errorContentResult.data.map((item) => {
      errorModalMessageObjectList.push({
        no: item.no,
        message: makeErrorMessage(
          { no: item.no },
          "ヘッダー行の列数と一致していません"
        ),
      });
    });
  }

  // ANS_IDが抜けている場合、エラー
  const nothingAnsIdResult = checkRequiredAnsId(array);
  if (nothingAnsIdResult.errorFlg) {
    nothingAnsIdResult.data.map((item) => {
      errorModalMessageObjectList.push({
        no: item.no,
        columnNo: MODAL_ANS_ID_COLUMN_NO,
        message: makeErrorMessage({ no: item.no }, "管理IDがありません"),
      });
    });
  }

  // ANS_IDに重複がある場合、エラー
  const duplicateAnsIdResult = checkDuplicationAnsId(
    array,
    errorModalMessageObjectList
  );
  if (duplicateAnsIdResult.errorFlg) {
    duplicateAnsIdResult.data.map((item) => {
      errorModalMessageObjectList.push({
        no: item.no,
        columnNo: MODAL_ANS_ID_COLUMN_NO,
        message: makeErrorMessage(
          { no: item.no },
          `管理ID ${item.ansId} が重複しています`
        ),
      });
    });
  }

  const interviewSeqColumnNo = potentialEmailColumnNo + (isPDH ? 1 : 0);
  const interviewDateTimeColumnNo = potentialEmailColumnNo + (isPDH ? 2 : 1);

  // アサイン日時空白チェック
  const blankAssignResult = checkBlankAssign(array, subjectType);
  if (blankAssignResult.errorFlg) {
    blankAssignResult.data.map((item) => {
      errorModalMessageObjectList.push({
        no: item.no,
        columnNo: interviewDateTimeColumnNo,
        message: makeErrorMessage(
          { no: item.no },
          "アサイン日時に空白があります"
        ),
      });
    });
  }

  // アサイン日時フォーマットチェック
  const errorDateResult = checkDateTimeFormat(
    array,
    errorModalMessageObjectList,
    interviewSeqColumnNo,
    interviewDateTimeColumnNo,
    subjectType
  );
  if (errorDateResult.errorFlg) {
    errorDateResult.data.map((item) => {
      errorModalMessageObjectList.push({
        no: item.no,
        columnNo: interviewDateTimeColumnNo,
        message: makeErrorMessage(
          { no: item.no },
          "日付・時刻の形式が正しくありません（日付：yyyy/MM/dd、時刻：HH:mm）"
        ),
      });
    });
  }

  // アサインしようとする実査がDBにない場合、エラー
  const doNotExistInterviewList = checkExistInterview(
    array,
    chatRoomList,
    errorModalMessageObjectList,
    interviewSeqColumnNo,
    interviewDateTimeColumnNo,
    subjectType
  );
  if (doNotExistInterviewList.errorFlg) {
    doNotExistInterviewList.data.map((item) => {
      errorModalMessageObjectList.push({
        no: item.no,
        columnNo: MONITOR_MODAL_INTERVIEW_ANS_AND_DATE_COLUMN_NO,
        message: makeErrorMessage(
          { no: item.no },
          "アサイン対象の実査が見つかりません。実査No.と日時を確認してください"
        ),
      });
    });
  }

  // 同時刻の実査が複数見つかり、実査No.が未入力の場合エラー
  const checkRequiredInterviewSeqExistsResult = checkRequiredInterviewSeqExists(
    array,
    chatRoomList,
    errorModalMessageObjectList,
    interviewSeqColumnNo,
    interviewDateTimeColumnNo,
    subjectType
  );
  if (checkRequiredInterviewSeqExistsResult.errorFlg) {
    checkRequiredInterviewSeqExistsResult.data.map((item) => {
      errorModalMessageObjectList.push({
        no: item.no,
        columnNo: MONITOR_MODAL_INTERVIEW_ANS_AND_DATE_COLUMN_NO,
        message: makeErrorMessage(
          { no: item.no },
          "同じ日時の実査が2件以上見つかりました。実査No.と日時を確認してください"
        ),
      });
    });
  }

  // 日時と実査No.の組み合わせが不整合のときエラー
  const checkDateAndNumberConsistenceResult = checkDateAndNumberConsistence(
    array,
    chatRoomList,
    errorModalMessageObjectList,
    interviewSeqColumnNo,
    interviewDateTimeColumnNo,
    subjectType
  );
  if (checkDateAndNumberConsistenceResult.errorFlg) {
    checkDateAndNumberConsistenceResult.data.map((item) => {
      errorModalMessageObjectList.push({
        no: item.no,
        columnNo: MONITOR_MODAL_INTERVIEW_ANS_AND_DATE_COLUMN_NO,
        message: makeErrorMessage(
          { no: item.no },
          "実査の日時とNo.が一致する実査が見つかりません。実査No.と日時を確認してください"
        ),
      });
    });
  }

  // 実施済の実査を別の実査にアサインする場合、エラー
  const changeAlreadyFinishedInterviewAssignList =
    checkAlreadyFinishedInterviewChange(
      array,
      fetchedMonitorList,
      errorModalMessageObjectList,
      interviewSeqColumnNo,
      interviewDateTimeColumnNo,
      subjectType
    );
  if (changeAlreadyFinishedInterviewAssignList.errorFlg) {
    changeAlreadyFinishedInterviewAssignList.data.map((item) => {
      errorModalMessageObjectList.push({
        no: item.no,
        columnNo: MONITOR_MODAL_INTERVIEW_ANS_AND_DATE_COLUMN_NO,
        message: makeErrorMessage(
          { no: item.no },
          "既に実施済の実査にアサインされています。実査No.と日時を確認してください"
        ),
      });
    });
  }

  if (isPDH) {
    const errorEmailResult = await checkEmailFormat(array);
    if (errorEmailResult.errorFlg) {
      errorEmailResult.data.map((item) => {
        errorModalMessageObjectList.push({
          no: item.no,
          columnNo: potentialEmailColumnNo,
          message: makeErrorMessage(
            { no: item.no },
            "メールアドレスのフォーマットが正しくありません"
          ),
        });
      });
    }
  }

  const returnList = errorModalMessageObjectList.sort((a, b) => {
    if (a.no < b.no) return -1;
    if (a.no > b.no) return 1;
    return 0;
  });

  return returnList;
};

/* メールアドレス重複判定関数 */
const checkDuplicationEmail = (array: string[][]) => {
  let warningFlg = false;
  const dataList: { no: number; email: string }[] = [];

  const emailList = [];
  for (let i = 0; i < array.length; i++) {
    if (i === 0) continue; //ヘッダー行はチェックしない
    const email = array[i][11];
    emailList.push(email);
  }

  for (let i = 0; i < emailList.length; i++) {
    let count = 0;
    const key = emailList[i];
    if (key === "") continue;
    for (let j = 0; j < emailList.length; j++) {
      if (key === emailList[j]) {
        count += 1;
      }
    }
    if (count > 1) {
      warningFlg = true;
      dataList.push({ no: i + 1, email: emailList[i] });
    }
  }

  const warningCheckResult: AlertCheckResultModal = {
    warningFlg,
    data: dataList,
  };

  return warningCheckResult;
};

const warningCheckMonitorBeforeImport = async (array: string[][]) => {
  const warningModalMessageObjectList: WarningModalMessageObject[] = [];

  const potentialEmailColumnNo = 3;

  // メールアドレスに重複がある場合、エラー
  const duplicateEmailResult = checkDuplicationEmail(array);
  if (duplicateEmailResult.warningFlg) {
    duplicateEmailResult.data.map((item) => {
      warningModalMessageObjectList.push({
        no: item.no,
        columnNo: potentialEmailColumnNo,
        message: makeErrorMessage(
          { no: item.no },
          `メールアドレス ${item.email} が重複しています`
        ),
      });
    });
  }
  const returnList = warningModalMessageObjectList.sort((a, b) => {
    if (a.no < b.no) return -1;
    if (a.no > b.no) return 1;
    return 0;
  });

  return returnList;
};

const changeCheckMonitorArrayBeforeImport = async (
  array: string[][],
  subjectId: string,
  fetchedMonitorList: CheckMonitor[],
  subjectType: InterviewType,
  isPDH: boolean
) => {
  const changeModalObjectList: ChangeModalObject[] = [];

  const potentialEmailColumnNo = 3;
  const otherChangesColumnNo = potentialEmailColumnNo + (isPDH ? 3 : 2);

  const potentialIncomingEmailIndex = isPDH ? 11 : 10;

  array.map((incomingMonitor, rowIndex) => {
    if (rowIndex === 0) return; //ヘッダー
    const targetMonitor = fetchedMonitorList.find(
      ({ ans_id }) => ans_id === incomingMonitor[0]
    );

    if (!targetMonitor) return;

    if (targetMonitor.sort_index !== rowIndex)
      changeModalObjectList.push({
        no: rowIndex,
        columnNo: 0,
        columnString: "No",
      });

    if (targetMonitor.assignedInterview) {
      if (
        targetMonitor.assignedInterview.interview_seq !==
        Number(incomingMonitor[6])
      ) {
        changeModalObjectList.push({
          no: rowIndex,
          columnNo: potentialEmailColumnNo + (isPDH ? 1 : 0),
          columnString: "実査No",
        });
      }

      if (
        targetMonitor.assignedInterview.scheduled_start_date !==
          incomingMonitor[2] ||
        targetMonitor.assignedInterview.scheduled_start_time !==
          incomingMonitor[3] ||
        (subjectType === InterviewType.TERM && //ワンタイムの時は 開始日=終了日 前提なので見ない
          targetMonitor.assignedInterview.scheduled_end_date !==
            incomingMonitor[4]) ||
        targetMonitor.assignedInterview.scheduled_end_time !==
          incomingMonitor[5]
      ) {
        changeModalObjectList.push({
          no: rowIndex,
          columnNo: potentialEmailColumnNo + (isPDH ? 2 : 1),
          columnString: "アサインチャットルーム",
        });
      }
    } else {
      if (incomingMonitor[6]) {
        changeModalObjectList.push({
          no: rowIndex,
          columnNo: potentialEmailColumnNo + (isPDH ? 1 : 0),
          columnString: "実査No",
        });
      }

      if (
        incomingMonitor[2] ||
        incomingMonitor[3] ||
        (subjectType === InterviewType.TERM && //ワンタイムの時は 開始日=終了日 前提なので見ない
          incomingMonitor[4]) ||
        incomingMonitor[5]
      ) {
        changeModalObjectList.push({
          no: rowIndex,
          columnNo: potentialEmailColumnNo + (isPDH ? 2 : 1),
          columnString: "アサインチャットルーム",
        });
      }
    }

    [
      {
        columnNo: 2,
        columnString: "名前",
        targetField: "name",
        targetIncomingIndex: potentialIncomingEmailIndex + 1,
      },
      isPDH && {
        columnNo: potentialEmailColumnNo,
        columnString: "メールアドレス",
        targetField: "email",
        targetIncomingIndex: potentialIncomingEmailIndex,
      },
      {
        columnNo: otherChangesColumnNo,
        columnString: "グループ / 割付",
        targetField: "classification",
        targetIncomingIndex: 1,
      },
      {
        columnNo: otherChangesColumnNo,
        columnString: "モニターID",
        targetField: "member_id",
        targetIncomingIndex: 10,
      },
      {
        columnNo: otherChangesColumnNo,
        columnString: "性別",
        targetField: "sex",
        targetIncomingIndex: potentialIncomingEmailIndex + 2,
      },
      {
        columnNo: otherChangesColumnNo,
        columnString: "年齢",
        targetField: "age",
        targetIncomingIndex: potentialIncomingEmailIndex + 3,
      },
      {
        columnNo: otherChangesColumnNo,
        columnString: "居住地",
        targetField: "address",
        targetIncomingIndex: potentialIncomingEmailIndex + 4,
      },
      {
        columnNo: otherChangesColumnNo,
        columnString: "既婚有無",
        targetField: "marriage",
        targetIncomingIndex: potentialIncomingEmailIndex + 5,
      },
      {
        columnNo: otherChangesColumnNo,
        columnString: "子有無",
        targetField: "children",
        targetIncomingIndex: potentialIncomingEmailIndex + 6,
      },
      {
        columnNo: otherChangesColumnNo,
        columnString: "職業",
        targetField: "job",
        targetIncomingIndex: potentialIncomingEmailIndex + 7,
      },
    ].map((column) => {
      column &&
        targetMonitor[column.targetField as keyof typeof targetMonitor] !==
          incomingMonitor[column.targetIncomingIndex] &&
        changeModalObjectList.push({
          no: rowIndex,
          columnNo: column.columnNo,
          columnString: column.columnString,
        });
    });

    // モニターの追加属性があった場合
    const incomingMonitorAttributeList = incomingMonitor.slice(
      potentialIncomingEmailIndex + 8,
      incomingMonitor.length
    );

    if (incomingMonitorAttributeList.length > 0) {
      const targetMonitorAttributeList = targetMonitor.monitorAttributeList;
      if (targetMonitorAttributeList) {
        incomingMonitorAttributeList.map(
          (attribute, i) =>
            attribute !== targetMonitorAttributeList[i].value &&
            changeModalObjectList.push({
              no: rowIndex,
              columnNo: otherChangesColumnNo,
              columnString: targetMonitorAttributeList[i].name,
            })
        );
      } else {
        const incomingMonitorAttributeHeaderList = array[0].slice(
          potentialIncomingEmailIndex + 8,
          incomingMonitor.length
        );
        incomingMonitorAttributeHeaderList.map((attributeHeader) =>
          changeModalObjectList.push({
            no: rowIndex,
            columnNo: otherChangesColumnNo,
            columnString: attributeHeader,
          })
        );
      }
    }
  });

  const returnList = changeModalObjectList.sort((a, b) => {
    if (a.no < b.no) return -1;
    if (a.no > b.no) return 1;
    return 0;
  });

  return returnList;
};

const newlyCheckMonitorArrayBeforeImport = async (
  array: string[][],
  fetchedMonitorList: CheckMonitor[]
) => {
  const newlyModalObjectList: NewlyModalObject[] = [];

  array.map((incomingMonitor, rowIndex) => {
    if (rowIndex === 0) return;
    const targetMonitor = fetchedMonitorList.find(
      ({ ans_id }) => ans_id === incomingMonitor[0]
    );
    !targetMonitor && newlyModalObjectList.push({ no: rowIndex });
  });

  const returnList = newlyModalObjectList.sort((a, b) => {
    if (a.no < b.no) return -1;
    if (a.no > b.no) return 1;
    return 0;
  });

  return returnList;
};

/** ------------------------------
 * input作成系
 *--------------------------------*/

const createMonitorInputFactory = (
  value: nameAndvalue[],
  index: number,
  subject_id: string,
  group_id: string,
  isPDH: boolean
): CreateMonitorInput => {
  const potentialIncomingEmailIndex = isPDH ? 11 : 10;
  return {
    subject_id: subject_id,
    ans_id: value[0].value,
    classification: value[1] ? value[1].value : null,
    member_id: value[10] ? value[10].value : null,
    name: value[potentialIncomingEmailIndex + 1]
      ? value[potentialIncomingEmailIndex + 1].value
      : null,
    sex: value[potentialIncomingEmailIndex + 2]
      ? value[potentialIncomingEmailIndex + 2].value
      : null,
    age: value[potentialIncomingEmailIndex + 3]
      ? value[potentialIncomingEmailIndex + 3].value
      : null,
    address: value[potentialIncomingEmailIndex + 4]
      ? value[potentialIncomingEmailIndex + 4].value
      : null,
    marriage: value[potentialIncomingEmailIndex + 5]
      ? value[potentialIncomingEmailIndex + 5].value
      : null,
    children: value[potentialIncomingEmailIndex + 6]
      ? value[potentialIncomingEmailIndex + 6].value
      : null,
    job: value[potentialIncomingEmailIndex + 7]
      ? value[potentialIncomingEmailIndex + 7].value
      : null,
    sort_index: index,
    groups: group_id,
  };
};

const updateMonitorInputFactory = (
  value: nameAndvalue[],
  index: number,
  monitor_id: string,
  isPDH: boolean
): UpdateMonitorInput => {
  const potentialIncomingEmailIndex = isPDH ? 11 : 10;
  return {
    id: monitor_id,
    ans_id: value[0].value,
    classification: value[1] ? value[1].value : null,
    member_id: value[10] ? value[10].value : null,
    name: value[potentialIncomingEmailIndex + 1]
      ? value[potentialIncomingEmailIndex + 1].value
      : null,
    sex: value[potentialIncomingEmailIndex + 2]
      ? value[potentialIncomingEmailIndex + 2].value
      : null,
    age: value[potentialIncomingEmailIndex + 3]
      ? value[potentialIncomingEmailIndex + 3].value
      : null,
    address: value[potentialIncomingEmailIndex + 4]
      ? value[potentialIncomingEmailIndex + 4].value
      : null,
    marriage: value[potentialIncomingEmailIndex + 5]
      ? value[potentialIncomingEmailIndex + 5].value
      : null,
    children: value[potentialIncomingEmailIndex + 6]
      ? value[potentialIncomingEmailIndex + 6].value
      : null,
    job: value[potentialIncomingEmailIndex + 7]
      ? value[potentialIncomingEmailIndex + 7].value
      : null,
    sort_index: index,
  };
};

const createMonitorAttributeInputFactory = (
  value: nameAndvalue,
  index: number,
  monitor_id: string,
  subject_id: string,
  group_id: string
): CreateMonitorAttributeInput => {
  return {
    subject_id: subject_id,
    monitor_id: monitor_id,
    seq_no: index,
    name: value.name,
    value: value.value,
    groups: group_id,
  };
};

const createMonitorAssignInputFactory = async (
  value: nameAndvalue[],
  subjectId: string,
  monitorId: string,
  chatRoomList: Interview[],
  groupId: string,
  subjectType: InterviewType
): Promise<CreateMonitorAssignInput | null> => {
  const startDate = value[2]?.value || null;
  const startTime = value[3]?.value || null;
  const endDate = value[4]?.value || null;
  const endTime = value[5]?.value || null;
  const interviewSeq = value[6]?.value || null;

  const createdRoom = chatRoomList.find((room) => {
    if (interviewSeq) {
      //実査No.がある場合は日時を見ない（同じ日時の実査をアサインしないようにするため）
      return room.interview_seq.toString() === interviewSeq;
    } else {
      //room.interview_typeが入力されていないデータがあるためsubjectTypeで分岐
      if (subjectType === InterviewType.ONE_TIME) {
        return (
          room.scheduled_start_date === startDate &&
          room.scheduled_start_time === startTime &&
          room.scheduled_end_time === endTime
        );
      } else if (subjectType === InterviewType.TERM) {
        return (
          room.scheduled_start_date === startDate &&
          room.scheduled_start_time === startTime &&
          room.scheduled_end_date === endDate &&
          room.scheduled_end_time === endTime
        );
      }
    }
  });

  if (createdRoom === undefined) {
    return null;
  }

  return {
    subject_id: subjectId,
    monitor_id: monitorId,
    interview_id: createdRoom.id,
    pin: makePinCode(),
    groups: groupId,
  };
};

/**------------------------------
 * 挿入系
 *-------------------------------*/
const createMonitor = async (
  value: nameAndvalue[],
  index: number,
  subjectId: string,
  groupId: string,
  isPDH: boolean
) => {
  const createMonitorInput = createMonitorInputFactory(
    value,
    index,
    subjectId,
    groupId,
    isPDH
  );
  const createdMonitorData = await createMonitorMutation(createMonitorInput);

  return createdMonitorData.data?.createMonitor.id;
};

const updateMonitor = async (
  value: nameAndvalue[],
  index: number,
  monitorId: string,
  isPDH: boolean
) => {
  const updateMonitorInput = updateMonitorInputFactory(
    value,
    index,
    monitorId,
    isPDH
  );
  await updateMonitorMutation(updateMonitorInput);
};

const createMonitorAttribute = async (
  value: nameAndvalue[],
  monitorId: string,
  subjectId: string,
  groupId: string,
  isPDH: boolean
) => {
  await Promise.all(
    value.map(async (v, index) => {
      const csvHeaderFixingSectionLength = isPDH ? 19 : 18;

      if (index < csvHeaderFixingSectionLength) return;
      const createMonitorAttributeInput = createMonitorAttributeInputFactory(
        v,
        index - csvHeaderFixingSectionLength + 1,
        monitorId,
        subjectId,
        groupId
      );
      await createMonitorAttributeMutation(createMonitorAttributeInput);
    })
  );
};

const createMonitorAssign = async (
  value: nameAndvalue[],
  subjectId: string,
  monitorId: string,
  chatRoomList: Interview[],
  groupId: string,
  subjectType: InterviewType
) => {
  const createMonitorAssignInput = await createMonitorAssignInputFactory(
    value,
    subjectId,
    monitorId,
    chatRoomList,
    groupId,
    subjectType
  );
  if (createMonitorAssignInput === null) return;
  await createMonitorAssignMutation(createMonitorAssignInput);
};

/** ------------------------------
 * 削除系
 *--------------------------------/ 

/* モニターアトリビュート全削除関数（案件） */
const deleteAllMonitorAttributeInTargetSubjectId = async (
  subjectId: string
) => {
  const targetMonitorAttributeList =
    await fetchMonitorAttributeBySubjectIdQuery(subjectId);
  if (!targetMonitorAttributeList) return;
  await Promise.all(
    targetMonitorAttributeList.map(async (targetMonitorAttribute) => {
      targetMonitorAttribute &&
        (await deleteMonitorAttributeMutation({
          id: targetMonitorAttribute.id,
        }));
    })
  );
};

/* モニターアトリビュート全削除関数（モニター） */
const deleteAllMonitorAttributeInTargetMonitorId = async (
  monitorId: string
) => {
  const targetMonitorAttributeList =
    await fetchMonitorAttributeByMonitorIdQuery(monitorId);
  if (!targetMonitorAttributeList) return;
  await Promise.all(
    targetMonitorAttributeList.map(async (targetMonitorAttribute) => {
      targetMonitorAttribute &&
        (await deleteMonitorAttributeMutation({
          id: targetMonitorAttribute.id,
        }));
    })
  );
};

/**---------------------------------------------
 * データ保存系
 *------------------------------------------------*/

/* モニターアサイン更新関数 */
const updateMonitorAssign = async (
  value: nameAndvalue[],
  subjectId: string,
  monitorId: string,
  chatRoomList: Interview[],
  groupId: string,
  subjectType: InterviewType
) => {
  // モニターアサインデータ（直近一つ）
  const latestAssignData = await fetchMonitorLatestAssignByMonitorIdQuery(
    monitorId
  );

  // アサインデータが存在しない場合、クリエイトし処理を終了する
  if (!latestAssignData) {
    createMonitorAssign(
      value,
      subjectId,
      monitorId,
      chatRoomList,
      groupId,
      subjectType
    );
    return;
  }

  // モニターアサインに紐づくインタビューデータ
  const targetInterview = await getInterviewByIdQuery(
    latestAssignData.interview_id
  );

  // アサインデータが存在し、紐づくインタビューが存在しない場合、何もせずリターン
  if (!targetInterview) return;

  // アサインデータが存在し、紐づくインタビューが開始済みの場合、何もせずリターン
  if (isInterviewStarted(targetInterview)) return;

  // アサインデータが存在し、紐づくインタビューが未実施かつ前回のインポート時とアサイン情報が同じ場合、何もせずリターン
  const importStartDate = value[2].value;
  const importStartTime = value[3].value;
  const importEndDate = value[4].value;
  const importEndTime = value[5].value;

  if (subjectType === InterviewType.ONE_TIME) {
    if (
      importStartDate === targetInterview.scheduled_start_date &&
      importStartTime === targetInterview.scheduled_start_time &&
      importEndTime === targetInterview.scheduled_end_time
    )
      return;
  } else if (subjectType === InterviewType.TERM) {
    if (
      importStartDate === targetInterview.scheduled_start_date &&
      importStartTime === targetInterview.scheduled_start_time &&
      importEndDate === targetInterview.scheduled_end_date &&
      importEndTime === targetInterview.scheduled_end_time
    )
      return;
  }

  // アサインデータが存在し、紐づくインタビューが未実施かつ前回のインポート時と異なる場合、前回のアサインデータを削除し、クリエイト
  await deleteMonitorAssignMutation({ id: latestAssignData.id });
  await createMonitorAssign(
    value,
    subjectId,
    monitorId,
    chatRoomList,
    groupId,
    subjectType
  );
};

/* メールアドレス更新関数 */
const updateEmail = async (
  targetMonitorId: string,
  targetEmailId: string | undefined,
  inputEmail: string,
  subjectId: string,
  groupId: string
) => {
  if (targetEmailId) {
    if (inputEmail) {
      await updateEmailMutation({
        monitor_id: targetMonitorId,
        email: inputEmail,
      });
    } else {
      await deleteEmailMutation({
        monitor_id: targetMonitorId,
      });
    }
  } else if (inputEmail) {
    await createEmailMutation({
      monitor_id: targetMonitorId,
      subject_id: subjectId,
      email: inputEmail,
      groups: groupId,
    });
  }
};

/* DB更新処理 */
const saveData = async (
  organizedMap: Map<string, nameAndvalue[]>,
  subject_id: string,
  group_id: string,
  subjectType: InterviewType,
  isPDH: boolean
) => {
  const idList = await fetchMonitorIdLIstInTargetSubject(subject_id);
  const importAnsIdList: string[] = [];
  const chatRoomList = await fetchInterviewList(subject_id);
  const emailList = isPDH ? await fetchEmailBySubjectIdQuery(subject_id) : [];

  let index = 0;
  for (const [key, value] of organizedMap.entries()) {
    index++;
    // DBに存在するが、CSVに存在しないAnsIdを特定するための準備
    importAnsIdList.push(key);

    // CSVの管理IDとDBのANS_IDを照合し、DBに居るモニターかどうかを判定する
    const targetMonitorId = idList.find(
      (item) => item.ansId === key
    )?.monitorId;

    if (targetMonitorId === undefined) {
      // 新規作成
      const createdMonitorId = await createMonitor(
        value,
        index,
        subject_id,
        group_id,
        isPDH
      );

      // 型ガード
      if (createdMonitorId === undefined) return;

      if (isPDH && value[11].value) {
        await createEmailMutation({
          monitor_id: createdMonitorId,
          subject_id,
          email: value[11].value,
          groups: group_id,
        });
      }

      await createMonitorAttribute(
        value,
        createdMonitorId,
        subject_id,
        group_id,
        isPDH
      );

      await createMonitorAssign(
        value,
        subject_id,
        createdMonitorId,
        chatRoomList,
        group_id,
        subjectType
      );

      continue;
    }
    // 更新
    // モニター（そのまま更新）
    await updateMonitor(value, index, targetMonitorId, isPDH);

    // モニターアトリビュート（全消しした後、新規作成する）
    await deleteAllMonitorAttributeInTargetMonitorId(targetMonitorId);
    await createMonitorAttribute(
      value,
      targetMonitorId,
      subject_id,
      group_id,
      isPDH
    );

    // モニターアサイン（別関数にて判定）
    await updateMonitorAssign(
      value,
      subject_id,
      targetMonitorId,
      chatRoomList,
      group_id,
      subjectType
    );

    // メールアドレス（別関数にて判定）
    if (isPDH) {
      const targetEmailId = emailList.find(
        ({ monitor_id }) => monitor_id === targetMonitorId
      )?.id;
      await updateEmail(
        targetMonitorId,
        targetEmailId,
        value[11].value,
        subject_id,
        group_id
      );
    }
  }
};

/**---------------------------------------------------------------------------
 * 
 * エクスポート
 * 
 --------------------------------------------------------------------------------*/
const decisionEntry = async (
  monitorAssign: MonitorAssign,
  preEntryTime: number
) => {
  const latestInputPin = monitorAssign.last_entry_date;
  if (!latestInputPin) return 0;

  const latestInputPinDateTime = new Date(latestInputPin);

  const targetInterview = await getInterviewByIdQuery(
    monitorAssign.interview_id
  );
  if (!targetInterview) return 0;

  const actualEndDate = targetInterview.actual_end_date;
  const actualEndTime = targetInterview.actual_end_time;
  if (!actualEndDate || !actualEndTime) return 0;

  const actualEndDateTime = new Date(`${actualEndDate} ${actualEndTime}:00`);

  const scheduledStartDate = targetInterview.scheduled_start_date;
  const scheduledStartTime = targetInterview.scheduled_start_time;
  if (!scheduledStartDate || !scheduledStartTime) return 0;

  const entryStartDateTime = new Date(
    `${scheduledStartDate} ${scheduledStartTime}:00`
  );
  entryStartDateTime.setMinutes(entryStartDateTime.getMinutes() - preEntryTime);

  if (
    latestInputPinDateTime >= entryStartDateTime &&
    latestInputPinDateTime <= actualEndDateTime
  )
    return 1;

  return 0;
};

/* */
const decisionEffectiveCancel = async (
  monitorAssign: MonitorAssign,
  preEntryTime: number,
  nexEntryTime: number
) => {
  const targetInterview = await getInterviewByIdQuery(
    monitorAssign.interview_id
  );
  if (!targetInterview) return 0;

  const scheduledStartDate = targetInterview.scheduled_start_date;
  const scheduledStartTime = targetInterview.scheduled_start_time;
  if (!scheduledStartDate || !scheduledStartTime) return 0;

  const entryStartDateTime = new Date(
    `${scheduledStartDate} ${scheduledStartTime}:00`
  );
  entryStartDateTime.setMinutes(entryStartDateTime.getMinutes() - preEntryTime);

  const entryEndDateTime = new Date(
    `${scheduledStartDate} ${scheduledStartTime}:00`
  );
  entryEndDateTime.setMinutes(entryEndDateTime.getMinutes() + nexEntryTime);

  const latestInputPin = monitorAssign.last_entry_date;
  if (!latestInputPin) return 0;
  const latestInputPinDateTime = new Date(latestInputPin);

  if (
    latestInputPinDateTime >= entryStartDateTime &&
    latestInputPinDateTime <= entryEndDateTime &&
    targetInterview.canceled === 3
  )
    return 1;

  return 0;
};

/* 実績決定処理 */
export const decideRecord = async (
  monitorId: string | undefined | null,
  interviewType?: InterviewType | null
) => {
  if (!monitorId || interviewType === InterviewType.TERM) return 0;

  const monitorAssignList = await fetchMonitorAssignByMonitorIdQuery(monitorId);
  if (monitorAssignList.length < 1) return 0;

  let pointSum = 0;
  await Promise.all(
    monitorAssignList.map(async (monitorAssign) => {
      const entry = await decisionEntry(monitorAssign, PRE_ENTRY_TIME_min);
      const cancel = await decisionEffectiveCancel(
        monitorAssign,
        PRE_ENTRY_TIME_min,
        WAIT_INTERVIEWER_TIME_min
      );

      const point = entry + cancel;
      pointSum += point;
    })
  );
  return pointSum;
};

/* モニター情報配列生成処理（エクスポート用） */
const createExportArray = async (
  subjectId: string,
  isPDH: boolean,
  interviewType?: InterviewType
) => {
  let exportMonitorList: (Monitor & { email?: string })[];
  if (isPDH) {
    const emailList = await fetchEmailBySubjectIdQuery(subjectId);
    exportMonitorList = (await fetchMonitorBysubjectIdQuery(subjectId)).map(
      (monitor) => {
        return {
          ...monitor,
          email: emailList.find(({ monitor_id }) => monitor.id === monitor_id)
            ?.email,
        };
      }
    );
  } else {
    exportMonitorList = await fetchMonitorBysubjectIdQuery(subjectId);
  }

  const exportArray = [];
  const header = await createCSVHeader(subjectId, isPDH);
  exportArray.push(header);

  for (let i = 0; i < exportMonitorList?.length; i++) {
    const monitorId = exportMonitorList[i].id;
    if (monitorId === undefined) continue;
    const assignData = await fetchMonitorLatestAssignByMonitorIdQuery(
      monitorId
    );

    // 固定部
    const array = [];
    if (exportMonitorList[i] === null) continue;

    array.push(exportMonitorList[i].ans_id);

    array.push(
      exportMonitorList[i].classification
        ? exportMonitorList[i].classification
        : ""
    );

    if (assignData === null || assignData === undefined) {
      array.push(""); //開始日
      array.push(""); //開始時刻
      array.push(""); //終了日
      array.push(""); //終了時刻
      array.push(""); //実査No.
      array.push(""); //URL
      array.push(""); //PIN
    } else {
      const interviewData = await getInterviewByIdQuery(
        assignData.interview_id
      );

      if (interviewData === null || interviewData === undefined) {
        array.push(""); //開始日
        array.push(""); //開始時刻
        array.push(""); //終了日
        array.push(""); //終了時刻
        array.push(""); //実査No.
        array.push(""); //URL
        array.push(""); //PIN
      } else {
        array.push(interviewData.scheduled_start_date?.replaceAll("-", "/"));
        array.push(interviewData.scheduled_start_time?.replaceAll("-", "/"));
        array.push(
          interviewType === InterviewType.ONE_TIME &&
            interviewData.scheduled_start_date ===
              interviewData.scheduled_end_date
            ? "-"
            : interviewData.scheduled_end_date?.replaceAll("-", "/")
        );
        array.push(interviewData.scheduled_end_time?.replaceAll("-", "/"));
        array.push(interviewData.interview_seq);
        array.push(getInterviewUrl(assignData.id));
        array.push(assignData.pin);
      }
    }

    array.push(await decideRecord(monitorId, interviewType));
    array.push(
      exportMonitorList[i].member_id ? exportMonitorList[i].member_id : ""
    );
    isPDH &&
      array.push(exportMonitorList[i].email ? exportMonitorList[i].email : "");
    array.push(exportMonitorList[i].name ? exportMonitorList[i].name : "");
    array.push(exportMonitorList[i].sex ? exportMonitorList[i].sex : "");
    array.push(exportMonitorList[i].age ? exportMonitorList[i].age : "");
    array.push(
      exportMonitorList[i].address ? exportMonitorList[i].address : ""
    );
    array.push(
      exportMonitorList[i].marriage ? exportMonitorList[i].marriage : ""
    );
    array.push(
      exportMonitorList[i].children ? exportMonitorList[i].children : ""
    );
    array.push(exportMonitorList[i].job ? exportMonitorList[i].job : "");

    // 可変部
    const variableList = await fetchMonitorAttributeByMonitorIdQuery(monitorId);
    if (variableList === undefined) {
      exportArray.push(array);
      continue;
    }
    variableList.map((variable) => {
      if (variable !== null) {
        array.push(variable.value ? variable.value : "");
      } else {
        array.push("");
      }
    });
    exportArray.push(array);
  }
  return exportArray;
};

/* エクスポート処理（クリップボードにコピー） */
const exportClipboard = async (
  exportArray: MonitorManagementTableValueType[][]
) => {
  const clipboardText = exportArray
    .map((record) => record.join("\t"))
    .join("\r\n");
  await navigator.clipboard.writeText(clipboardText);
  await navigator.clipboard.readText();
};

/**---------------------------------------------------------------------------
 * 
 * 画面表示
 * 
 --------------------------------------------------------------------------------*/

const createDisplayMonitorList = async (subjectId: string) => {
  const monitorList = await fetchMonitorBysubjectIdQuery(subjectId);
  if (monitorList === undefined) return;

  const monitorAssignList = await fetchMonitorAssignBysubjectIdQuery(subjectId);
  const sortedMonitorAssignList = [...monitorAssignList].sort((a, b) => {
    if (a.createdAt > b.createdAt) return -1;
    if (a.createdAt < b.createdAt) return 1;
    return 0;
  });

  const interviewList = await fetchInterviewList(subjectId);

  const displayMonitorList = monitorList.map((monitor, index) => {
    const latestMonitorAssign = sortedMonitorAssignList.filter(
      (monitorAssign) => monitorAssign.monitor_id === monitor.id
    )[0];

    const assignedInterview =
      latestMonitorAssign &&
      interviewList.find(
        (interview) => interview.id === latestMonitorAssign.interview_id
      );
    const isAssignValid =
      !!assignedInterview && assignedInterview.canceled === 0;

    return {
      id: monitor.id,
      ansId: monitor.ans_id,
      classfication: monitor.classification ? monitor.classification : null,
      name: monitor.name ? monitor.name : null,
      sex: monitor.sex ? monitor.sex : null,
      age: monitor.age ? monitor.age : null,
      job: monitor.job ? monitor.job : null,
      address: monitor.address ? monitor.address : null,
      marriage: monitor.marriage ? monitor.marriage : null,
      children: monitor.children ? monitor.children : null,
      assignedInterviewId: isAssignValid ? assignedInterview.id : "0",
      assignedInterview: isAssignValid ? assignedInterview : undefined,
      memo: monitor.memo || "",
      interviewUrl: latestMonitorAssign?.id
        ? getInterviewUrl(latestMonitorAssign.id)
        : "",
      interviewPinCode: latestMonitorAssign?.pin || "",
      No: monitor.sort_index?.toString() || (index + 1).toString(),
      login: !!latestMonitorAssign?.last_entry_date,
    };
  });

  return displayMonitorList;
};

const assignOnDisplay = async (
  subjectId: string,
  interviewId: string,
  groupId: string,
  moniorId: string,
  previousInteriewId: string | undefined
) => {
  // 型ガード
  // if (previousInteriewId === undefined) return;

  // 同じインタビューの場合、処理しない
  // if (interviewId === previousInteriewId) return;

  // 選択されたインタビューが存在しない場合、何もしない（起こらない）
  const assignedInterview = await getInterviewByIdQuery(interviewId);
  if (
    (assignedInterview === undefined || assignedInterview === null) &&
    interviewId !== "0"
  )
    return;

  const monitorAssignData = await fetchMonitorLatestAssignByMonitorIdQuery(
    moniorId
  );

  // モニターアサインにデータが無い場合
  if (monitorAssignData === undefined || monitorAssignData === null) {
    // 未アサインー＞未アサイン
    if (interviewId === "0") return;

    //未アサインー＞アサイン
    await createMonitorAssignMutation({
      subject_id: subjectId,
      monitor_id: moniorId,
      interview_id: interviewId,
      pin: makePinCode(),
      groups: groupId,
    });
    return;
  } else {
    // 以下モニターアサインにデータがある場合
    const previousAssignedInterview = await getInterviewByIdQuery(
      previousInteriewId
    );

    //アサインされていたインタビューが未実施かつ、キャンセルされたものでなければアサインを削除（実績にはんえいされないデータ）
    if (
      previousAssignedInterview?.canceled === 0 &&
      !isInterviewStarted(previousAssignedInterview)
    ) {
      await deleteMonitorAssignMutation({ id: monitorAssignData.id });
    }

    // アサイン→未アサイン
    if (interviewId === "0") {
      return;
    }

    // 追加
    await createMonitorAssignMutation({
      subject_id: subjectId,
      monitor_id: moniorId,
      interview_id: interviewId,
      pin: makePinCode(),
      groups: groupId,
    });
  }
};

export {
  createExportArray,
  exportClipboard,
  createDisplayMonitorList,
  assignOnDisplay,
  convertTSVStringToTwoDimensionalArray,
  detectHeaderChange,
  deleteAllMonitorAttributeInTargetSubjectId,
  checkMonitorArrayBeforeImport,
  warningCheckMonitorBeforeImport,
  changeCheckMonitorArrayBeforeImport,
  newlyCheckMonitorArrayBeforeImport,
  createOrganizedMap,
  saveData,
};
